import { useMemo } from 'react';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { Divider, TextField, Typography } from '@mui/material';
import { usePartnerGetEncounterHeaderVoidReasonList } from 'hooks/requests/usePartnerGetEncounterHeaderVoidReasonList';
import { usePartnerVoidEncounter } from 'hooks/requests/usePartnerVoidEncounter';

function PaperComponent(props) {
  return (
    <Draggable
      handle="#void-dialog"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiPaper-root.MuiDialog-paper {
    background-color: #f5f5f5;
    border: 6px solid #95bcd7;
    padding: 10px;
  }
`;
const StyledDialogContent = styled(DialogContent)`
  width: 550px;
`;

const StyledFormControl = styled(FormControl)`
  && {
    margin-top: 20px;
  }
`;

const NewComment = ({ id, newComment, placeholder, onChange }) => (
  <StyledFormControl fullWidth>
    <TextField
      id={id}
      cols="40"
      multiline
      variant="outlined"
      label="Comment (optional)"
      rows="4"
      placeholder={placeholder}
      onChange={onChange}
      value={newComment}
      sx={{ backgroundColor: 'white' }}
    />
  </StyledFormControl>
);

const ControlsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 10px;
`;

export default function VoidDialog({
  show,
  voidReasons,
  optionalComment,
  onSave,
  onClose,
  handleOptionalCommentChange,
  handleVoidReasonChange,
  selectedVoidReason,
  isIBIS,
  isMulti = false,
  applicationID = undefined,
  headerIDs = undefined
}) {
  // Use the new hook to fetch void reasons
  const { data: fetchedVoidReasons } =
    usePartnerGetEncounterHeaderVoidReasonList(
      isMulti ? applicationID || '1' : '1'
    );

  // Use the new hook for voiding encounters
  const { mutateAsync: voidEncounter } = usePartnerVoidEncounter(isMulti);

  // Use either fetched reasons or passed reasons based on isMulti flag
  const menuReasons = isMulti ? fetchedVoidReasons : voidReasons;

  const handleSave = async () => {
    const result = window.confirm(
      `You have selected ${
        headerIDs?.length ?? 1
      } encounter(s) to void. Are you sure you want to proceed with voiding ${
        headerIDs?.length ?? 1
      } encounter(s)? `
    );
    if (!result) {
      return;
    }
    await voidEncounter({
      encounterHeaderIdentifier: isMulti ? headerIDs : headerIDs?.[0] ?? '',
      encounterHeaderVoidReasonID:
        selectedVoidReason.encounterHeaderVoidReasonID,
      comment: optionalComment
    });
    onSave();
  };

  return (
    <StyledDialog
      open={show}
      onClose={onClose}
      PaperComponent={PaperComponent}
      aria-labelledby="void-dialog"
    >
      <DialogTitle id="void-dialog" style={{ cursor: 'move' }}>
        Void Request
        <Typography color="error">
          Voiding {headerIDs?.length ?? 1} Encounter(s)
        </Typography>
      </DialogTitle>
      <Divider />
      <StyledDialogContent>
        <InputLabel id="selected-reason" style={{ marginBottom: '5px' }}>
          Select Reason
        </InputLabel>
        <FormControl required variant="outlined" style={{ width: '100%' }}>
          <Select
            id="void-reason-select"
            style={{ backgroundColor: 'white' }}
            labelId="selected-reason"
            value={selectedVoidReason}
            onChange={handleVoidReasonChange}
            input={<OutlinedInput />}
          >
            {!!menuReasons &&
              menuReasons.map(el => (
                <MenuItem key={el.encounterHeaderVoidReasonID} value={el}>
                  {el.encounterHeaderVoidReason}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {!isIBIS && (
          <NewComment
            id="void-newComment"
            newComment={optionalComment}
            onChange={handleOptionalCommentChange}
          />
        )}
        <ControlsContainer>
          <Button
            id="void-save"
            variant="contained"
            color="primary"
            disabled={selectedVoidReason === null}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            id="void-close"
            style={{ marginLeft: '5px' }}
            variant="outlined"
            color="primary"
            onClick={onClose}
          >
            Close
          </Button>
        </ControlsContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
}
